/**
 * Admin Returns & Refunds — workflow board for return requests.
 *
 * Backend: /api/admin/returns (list, create, status updates, delete).
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatPrice } from "@/lib/api";
import {
    Plus,
    Loader2,
    UndoDot,
    Check,
    X,
    PackageCheck,
    BadgeIndianRupee,
    Trash2,
    Search,
    ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { ExportButtons } from "@/lib/exportData";
import Pagination, { usePagination } from "@/components/admin/Pagination";

const STATUS_OPTIONS = ["all", "requested", "approved", "rejected", "received", "refunded", "cancelled"];

const STATUS_STYLES = {
    requested: "bg-[#FDF1E1] text-[#8A6A2A] border-[#F0E0C0]",
    approved: "bg-[#E7F1EA] text-[#2F6F4A] border-[#C8E0CF]",
    rejected: "bg-[#FBE6E6] text-[#9A2C2C] border-[#F1C4C4]",
    received: "bg-[#E5EEF7] text-[#2A557C] border-[#C5D9EC]",
    refunded: "bg-[#EDE4F5] text-[#5A2F87] border-[#D7C5EC]",
    cancelled: "bg-[#F1EEE9] text-[#6B645C] border-[#E7DED2]",
};

function StatusPill({ status }) {
    const cls = STATUS_STYLES[status] || "bg-[#F1EEE9] text-[#6B645C] border-[#E7DED2]";
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-mono-ui uppercase tracking-[0.12em] border ${cls}`}
        >
            {status}
        </span>
    );
}

function NewReturnDialog({ open, onClose, onCreated }) {
    const [orders, setOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [pickedItems, setPickedItems] = useState({}); // key: variant_sku → qty
    const [reason, setReason] = useState("");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [q, setQ] = useState("");

    useEffect(() => {
        if (!open) return;
        setLoadingOrders(true);
        api.get("/admin/orders")
            .then((r) => setOrders(r.data || []))
            .finally(() => setLoadingOrders(false));
        setSelectedOrder(null);
        setPickedItems({});
        setReason("");
        setNotes("");
        setQ("");
    }, [open]);

    const filteredOrders = useMemo(() => {
        if (!q) return orders.slice(0, 50);
        const lower = q.toLowerCase();
        return orders
            .filter(
                (o) =>
                    (o.order_number || "").toLowerCase().includes(lower) ||
                    (o.customer_email || "").toLowerCase().includes(lower) ||
                    (o.customer_name || o.address?.full_name || "")
                        .toLowerCase()
                        .includes(lower)
            )
            .slice(0, 50);
    }, [orders, q]);

    const submit = async () => {
        if (!selectedOrder) return toast.error("Pick an order");
        const items = Object.entries(pickedItems)
            .filter(([, qty]) => Number(qty) > 0)
            .map(([sku, qty]) => {
                const it = selectedOrder.items.find((x) => x.variant_sku === sku);
                return {
                    product_id: it?.product_id,
                    variant_sku: sku,
                    qty: Number(qty),
                    reason,
                };
            });
        if (items.length === 0) return toast.error("Pick at least one item");
        setSubmitting(true);
        try {
            const { data } = await api.post("/admin/returns", {
                order_id: selectedOrder.id,
                items,
                reason,
                notes,
            });
            toast.success("Return created");
            onCreated(data);
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Create failed");
        } finally {
            setSubmitting(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="new-return-dialog">
            <div className="bg-white rounded-2xl border border-[#E7DED2] max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                <header className="px-5 py-4 border-b border-[#E7DED2] flex items-center gap-3">
                    <UndoDot className="h-4 w-4 text-[#857C72]" />
                    <p className="font-display text-xl flex-1">New return request</p>
                    <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-[#FFFCF7] flex items-center justify-center">
                        <X className="h-4 w-4" />
                    </button>
                </header>
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    {!selectedOrder ? (
                        <>
                            <div className="relative">
                                <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#857C72]" />
                                <input
                                    placeholder="Search order # / customer email / name…"
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    className="w-full pl-9 pr-3 py-2 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-sm"
                                    data-testid="new-return-order-search"
                                />
                            </div>
                            <div className="rounded-2xl border border-[#E7DED2] divide-y divide-[#F4EFE6] max-h-80 overflow-y-auto">
                                {loadingOrders ? (
                                    <div className="p-4 text-xs text-[#857C72] flex items-center gap-2">
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading orders…
                                    </div>
                                ) : filteredOrders.length === 0 ? (
                                    <div className="p-4 text-xs text-[#857C72]">No orders match.</div>
                                ) : (
                                    filteredOrders.map((o) => (
                                        <button
                                            key={o.id}
                                            onClick={() => setSelectedOrder(o)}
                                            className="w-full text-left px-4 py-3 hover:bg-[#FFFCF7] flex items-center gap-3"
                                            data-testid={`new-return-pick-order-${o.order_number || o.id}`}
                                        >
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-display truncate">
                                                    {o.order_number || o.id}{" "}
                                                    <span className="text-[10px] font-mono-ui text-[#857C72] ml-1 uppercase tracking-[0.1em]">
                                                        {o.status}
                                                    </span>
                                                </p>
                                                <p className="text-[10px] font-mono-ui text-[#857C72] truncate">
                                                    {(o.customer_name || o.address?.full_name || "—")} · {o.customer_email || "—"} · {o.items.length} items · {formatPrice(o.total)}
                                                </p>
                                            </div>
                                            <ArrowRight className="h-3.5 w-3.5 text-[#857C72]" />
                                        </button>
                                    ))
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="rounded-2xl bg-[#FFFCF7] border border-[#E7DED2] p-4">
                                <p className="text-[10px] font-mono-ui text-[#857C72] uppercase tracking-[0.12em]">Order</p>
                                <p className="font-display text-lg">
                                    {selectedOrder.order_number || selectedOrder.id}
                                </p>
                                <p className="text-xs text-[#6B645C]">
                                    {(selectedOrder.customer_name || selectedOrder.address?.full_name || "—")} · {selectedOrder.customer_email || "—"} · {formatPrice(selectedOrder.total)}
                                </p>
                                <button
                                    onClick={() => setSelectedOrder(null)}
                                    className="text-[10px] font-mono-ui text-[#857C72] underline mt-1"
                                >
                                    change order
                                </button>
                            </div>
                            <div className="space-y-2">
                                <p className="text-[11px] font-mono-ui tracking-[0.15em] uppercase text-[#857C72]">Items to return</p>
                                {selectedOrder.items.map((it) => {
                                    const max = it.qty;
                                    const v = pickedItems[it.variant_sku] || 0;
                                    return (
                                        <div key={it.variant_sku} className="flex items-center gap-3 p-3 rounded-2xl border border-[#E7DED2] bg-white" data-testid={`new-return-item-${it.variant_sku}`}>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm truncate font-medium">{it.product_title}</p>
                                                <p className="text-[10px] font-mono-ui text-[#857C72] truncate">
                                                    {it.variant_sku} · {formatPrice(it.price)} · purchased {max}
                                                </p>
                                            </div>
                                            <input
                                                type="number"
                                                min={0}
                                                max={max}
                                                value={v}
                                                onChange={(e) =>
                                                    setPickedItems((prev) => ({
                                                        ...prev,
                                                        [it.variant_sku]: Math.max(0, Math.min(max, parseInt(e.target.value || "0", 10))),
                                                    }))
                                                }
                                                className="w-20 px-3 py-1.5 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-sm text-center"
                                                data-testid={`new-return-qty-${it.variant_sku}`}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <input
                                    placeholder="Reason (size, defect, late, etc.)"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    className="px-4 py-2 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-sm"
                                />
                                <input
                                    placeholder="Internal notes"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    className="px-4 py-2 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-sm"
                                />
                            </div>
                        </>
                    )}
                </div>
                <footer className="px-5 py-4 border-t border-[#E7DED2] flex items-center gap-2">
                    <button
                        onClick={onClose}
                        className="inline-flex items-center gap-1.5 px-4 h-9 rounded-full bg-white border border-[#E7DED2] hover:bg-[#FFFCF7] text-xs font-mono-ui"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={submit}
                        disabled={!selectedOrder || submitting}
                        className="ml-auto dw-btn-primary inline-flex items-center gap-1.5 px-4 h-9 text-xs font-mono-ui disabled:opacity-50"
                        data-testid="new-return-submit"
                    >
                        {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Create return
                    </button>
                </footer>
            </div>
        </div>
    );
}

export default function AdminReturns() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [q, setQ] = useState("");
    const [dialog, setDialog] = useState(false);
    const [busy, setBusy] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const url = filter === "all" ? "/admin/returns" : `/admin/returns?status=${filter}`;
            const { data } = await api.get(url);
            setItems(data || []);
        } catch {
            toast.error("Failed to load returns");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, [filter]);

    const updateStatus = async (id, status, refundAmount) => {
        setBusy(id + status);
        try {
            const body = { status };
            if (refundAmount != null) body.refund_amount = refundAmount;
            const { data } = await api.post(`/admin/returns/${id}/status`, body);
            setItems((curr) => curr.map((r) => (r.id === id ? data : r)));
            toast.success(`Marked ${status}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update failed");
        } finally {
            setBusy(null);
        }
    };

    const remove = async (r) => {
        if (!window.confirm(`Delete return ${r.id.slice(0, 8)}?`)) return;
        try {
            await api.delete(`/admin/returns/${r.id}`);
            setItems((curr) => curr.filter((x) => x.id !== r.id));
            toast.success("Deleted");
        } catch {
            toast.error("Delete failed");
        }
    };

    const filtered = useMemo(
        () =>
            items.filter(
                (r) =>
                    !q ||
                    (r.order_number || "").toLowerCase().includes(q.toLowerCase()) ||
                    (r.customer_email || "").toLowerCase().includes(q.toLowerCase()) ||
                    (r.customer_name || "").toLowerCase().includes(q.toLowerCase()) ||
                    (r.reason || "").toLowerCase().includes(q.toLowerCase())
            ),
        [items, q]
    );
    const { page, pageSize, pageItems, setPage, setPageSize } = usePagination(filtered, 20);

    const exportRows = useMemo(
        () =>
            items.map((r) => ({
                id: r.id,
                order_number: r.order_number || r.order_id,
                customer: r.customer_name,
                email: r.customer_email,
                status: r.status,
                items_count: (r.items || []).length,
                refund_amount_inr: (r.refund_amount || 0) / 100,
                refunded_amount_inr: (r.refunded_amount || 0) / 100,
                stock_restored: r.stock_restored,
                reason: r.reason,
                created_at: r.created_at,
            })),
        [items]
    );

    return (
        <div className="space-y-6" data-testid="admin-returns">
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <p className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[#857C72]">Orders</p>
                    <h1 className="font-display text-3xl sm:text-4xl mt-1">Returns &amp; refunds</h1>
                    <p className="text-xs text-[#857C72] mt-1">
                        {items.length} request{items.length === 1 ? "" : "s"} · approving a return restores stock automatically when goods are received or refunded.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        onClick={() => setDialog(true)}
                        className="dw-btn-primary inline-flex items-center gap-2 px-4 h-10 font-mono-ui text-xs"
                        data-testid="add-return-btn"
                    >
                        <Plus className="h-4 w-4" /> New return
                    </button>
                    <ExportButtons rows={exportRows} filename="xplosiv_booking_changes" columns={Object.keys(exportRows[0] || {})} />
                </div>
            </header>

            <div className="flex flex-wrap items-center gap-2">
                <input
                    type="text"
                    placeholder="Search order #, customer, reason…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="px-4 py-2 rounded-full bg-white border border-[#E7DED2] text-sm flex-1 min-w-[220px] max-w-md"
                    data-testid="returns-search"
                />
                <div className="flex flex-wrap items-center gap-1.5">
                    {STATUS_OPTIONS.map((s) => (
                        <button
                            key={s}
                            onClick={() => setFilter(s)}
                            className={`px-3 py-1.5 rounded-full text-[11px] font-mono-ui uppercase tracking-[0.12em] border transition-colors ${
                                filter === s
                                    ? "bg-[#1B1B1F] text-white border-[#1B1B1F]"
                                    : "bg-white text-[#3D3934] border-[#E7DED2] hover:bg-[#FFFCF7]"
                            }`}
                            data-testid={`returns-filter-${s}`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-10 text-[#857C72]">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
                </div>
            ) : pageItems.length === 0 ? (
                <div className="rounded-2xl border-2 border-dashed border-[#E7DED2] p-10 text-center text-[#857C72]">
                    <UndoDot className="h-8 w-8 mx-auto mb-3 opacity-60" />
                    No return requests {filter === "all" ? "yet" : `with status "${filter}"`}.
                </div>
            ) : (
                <div className="rounded-2xl border border-[#E7DED2] bg-white overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[10px] font-mono-ui uppercase tracking-[0.12em] text-[#857C72] border-b border-[#E7DED2] bg-[#FFFCF7]">
                                    <th className="py-3 px-4">Return</th>
                                    <th>Order</th>
                                    <th>Customer</th>
                                    <th>Items</th>
                                    <th>Refund</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pageItems.map((r) => {
                                    const next = r.status;
                                    return (
                                        <tr
                                            key={r.id}
                                            className="border-b border-[#F4EFE6] hover:bg-[#FFFCF7]"
                                            data-testid={`return-row-${r.id}`}
                                        >
                                            <td className="py-3 px-4 font-mono-ui text-[11px]">
                                                {r.id.slice(0, 8)}
                                                <div className="text-[10px] text-[#857C72]">
                                                    {(r.created_at || "").slice(0, 10)}
                                                </div>
                                            </td>
                                            <td>
                                                <p className="text-xs font-display">{r.order_number || r.order_id}</p>
                                            </td>
                                            <td>
                                                <p className="text-xs">{r.customer_name || "—"}</p>
                                                <p className="text-[10px] text-[#857C72]">{r.customer_email || "—"}</p>
                                            </td>
                                            <td className="text-xs">
                                                {(r.items || []).length}
                                                <div className="text-[10px] text-[#857C72] max-w-[180px] truncate">
                                                    {(r.items || []).map((i) => `${i.variant_sku}×${i.qty}`).join(", ")}
                                                </div>
                                            </td>
                                            <td className="text-xs">
                                                {formatPrice(r.refund_amount || 0)}
                                                {r.refunded_amount > 0 && (
                                                    <div className="text-[10px] text-[#2F6F4A]">
                                                        paid {formatPrice(r.refunded_amount)}
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <StatusPill status={r.status} />
                                                {r.stock_restored && (
                                                    <div className="text-[9px] font-mono-ui text-[#2F6F4A] mt-1">
                                                        stock restored
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <div className="flex flex-wrap items-center gap-1.5">
                                                    {next === "requested" && (
                                                        <>
                                                            <button
                                                                onClick={() => updateStatus(r.id, "approved")}
                                                                disabled={busy === r.id + "approved"}
                                                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#1B1B1F] text-white text-[10px] font-mono-ui hover:opacity-90 disabled:opacity-50"
                                                                data-testid={`return-approve-${r.id}`}
                                                            >
                                                                <Check className="h-3 w-3" /> Approve
                                                            </button>
                                                            <button
                                                                onClick={() => updateStatus(r.id, "rejected")}
                                                                disabled={busy === r.id + "rejected"}
                                                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-[#E7DED2] hover:bg-red-50 hover:text-red-600 text-[10px] font-mono-ui"
                                                                data-testid={`return-reject-${r.id}`}
                                                            >
                                                                <X className="h-3 w-3" /> Reject
                                                            </button>
                                                        </>
                                                    )}
                                                    {next === "approved" && (
                                                        <button
                                                            onClick={() => updateStatus(r.id, "received")}
                                                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-[#E7DED2] hover:bg-[#FFFCF7] text-[10px] font-mono-ui"
                                                            data-testid={`return-receive-${r.id}`}
                                                        >
                                                            <PackageCheck className="h-3 w-3" /> Mark received
                                                        </button>
                                                    )}
                                                    {(next === "approved" || next === "received") && (
                                                        <button
                                                            onClick={() => updateStatus(r.id, "refunded")}
                                                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#2F6F4A] text-white text-[10px] font-mono-ui hover:opacity-90"
                                                            data-testid={`return-refund-${r.id}`}
                                                        >
                                                            <BadgeIndianRupee className="h-3 w-3" /> Refund
                                                        </button>
                                                    )}
                                                    {(next === "requested" || next === "approved") && (
                                                        <button
                                                            onClick={() => updateStatus(r.id, "cancelled")}
                                                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-[#E7DED2] hover:bg-[#FFFCF7] text-[10px] font-mono-ui"
                                                        >
                                                            Cancel
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => remove(r)}
                                                        className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border border-[#E7DED2] hover:bg-red-50 hover:text-red-600"
                                                        title="Delete"
                                                        data-testid={`return-delete-${r.id}`}
                                                    >
                                                        <Trash2 className="h-3 w-3" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            <Pagination page={page} pageSize={pageSize} total={filtered.length} onPage={setPage} onPageSize={setPageSize} />

            <NewReturnDialog open={dialog} onClose={() => setDialog(false)} onCreated={(d) => setItems((curr) => [d, ...curr])} />
        </div>
    );
}
